function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Callout, ConfirmationModal, Drawer, Spinner, Typography, useDidUpdate } from 'seagull';
import { useGlobalContext, useIoContext, useStoreActions } from "../../../contexts";
import { openInNewTabProps, routes } from "../../../constants";
import ScheduledInvoice from "./ScheduledInvoice/ScheduledInvoice";
import BillingControl from "./BillingControl/BillingControl";
import BillingControlFooter from "./BillingControl/BillingControlFooter";
import MinisiteTabHeader from "../MinisiteTabHeader/MinisiteTabHeader";
import styles from "./BillingHub.module.scss";
import { getEmptyState, getInvoiceDateInfo, getLineItemsProps, getNoInvoicesText, isServiceRelevantForBillingHub } from "./BillingHub.utils";
import { billingControlDataModel, serviceModel, timeModel } from "../../../models";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import RestfulWrapper from "../../RestfulWrapper/RestfulWrapper";
import difference from 'lodash/difference';
import first from 'lodash/first';
import DisabledBillingTooltip from "../../DisabledBillingTooltip";
import InvoiceNavigator from "./InvoiceNavigator/InvoiceNavigator";
import EmptyState from "../../EmptyState/EmptyState";
import { RestfulStrategy, useRestfulWrapper } from "../../../hooks/useRestfulWrapper";
import GenericError from "../../GenericError/GenericError";
function BillingHubWithData(props) {
  const {
    agreement,
    billingData,
    isVendor,
    onRefresh: refreshBillingHubData
  } = props;
  const {
    invoiceDraftDates,
    deliverablesBillCommandsMap,
    billingControlData
  } = billingData;
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  const {
    DISABLE_BILLING_SCAN
  } = useFeatureFlags();
  const globalContext = useGlobalContext();
  const lineItemIds = useRef();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(invoiceDraftDates[0] || null);
  const {
    loading: loadingInvoiceDraft,
    refresh: refreshInvoiceDraft,
    renderRestfulState: renderScheduledInvoice
  } = useRestfulWrapper({
    fetch: fetchInvoiceDraft
  });
  const invoiceDateInfo = getInvoiceDateInfo(selectedDate, billingData.invoiceDraftDates);
  const isActiveAgreement = useMemo(() => {
    return !agreement.effectiveDate || timeModel.fromDatestampObject(agreement.effectiveDate).getTime() <= getNow().getTime();
  }, [agreement.effectiveDate]);
  const servicesRequiringDeliveryApproval = useMemo(() => {
    return agreement.services.filter(s => {
      var _deliverablesBillComm;
      return ((_deliverablesBillComm = deliverablesBillCommandsMap[s.id]) === null || _deliverablesBillComm === void 0 || (_deliverablesBillComm = _deliverablesBillComm[0]) === null || _deliverablesBillComm === void 0 ? void 0 : _deliverablesBillComm.status) === 'requiresApproval';
    });
  }, [agreement, deliverablesBillCommandsMap]);
  useEffect(() => {
    setSelectedDate(prevSelectedDate => {
      if (!prevSelectedDate && invoiceDraftDates !== null && invoiceDraftDates !== void 0 && invoiceDraftDates.length) {
        return invoiceDraftDates[0];
      } else if (prevSelectedDate && !(invoiceDraftDates !== null && invoiceDraftDates !== void 0 && invoiceDraftDates.length)) {
        return null;
      } else {
        return prevSelectedDate;
      }
    });
  }, [invoiceDraftDates]);
  useDidUpdate(() => {
    refreshInvoiceDraft();
  }, [selectedDate]);
  async function fetchInvoiceDraft() {
    const getAddedLineItemId = newInvoiceDraft => {
      // used for blink animation
      const newLineItemIds = newInvoiceDraft.lineItems.map(_ref => {
        let {
          id
        } = _ref;
        return id;
      });
      const newAddedLineItemId = lineItemIds.current ? first(difference(newLineItemIds, lineItemIds.current)) : undefined;
      lineItemIds.current = newLineItemIds;
      return newAddedLineItemId;
    };
    if (selectedDate) {
      const newInvoiceDraft = await api.invoiceDraft.getByDate(agreement.id, selectedDate);
      return {
        invoiceDraft: newInvoiceDraft,
        addedLineItemId: newInvoiceDraft ? getAddedLineItemId(newInvoiceDraft) : undefined
      };
    } else {
      return {
        invoiceDraft: null
      };
    }
  }
  async function onApprove(chargeId) {
    logBI(10569);
    const loaderName = 'approveCharge';
    globalContext.startLoader(loaderName);
    try {
      await api.charge.approve(agreement.id, chargeId);
      await refreshAllData();
      globalContext.showSuccessToast('Charge approved', 'approve-success-toast');
    } catch (err) {
      globalContext.showErrorToast('Failed to approve charge', 'approve-failure-toast');
    } finally {
      globalContext.stopLoader(loaderName);
    }
  }
  async function onRemove(chargeId, options) {
    const {
      billLaterCommandId,
      serviceId
    } = options;
    const confirmed = await globalContext.showModal({
      closeOnClickOutside: true,
      shouldBlurBackground: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/React.createElement(ConfirmationModal, {
          skin: "danger",
          title: "Remove line item",
          content: "Are you sure you want to remove this line item?",
          primaryButtonLabel: "Remove",
          secondaryButtonLabel: "Cancel",
          onPrimaryButtonClick: () => dismissModal(true),
          onSecondaryButtonClick: () => dismissModal(false)
        });
      }
    });
    if (!confirmed) return;
    const loaderName = 'removeCharge';
    globalContext.startLoader(loaderName);
    const isService = !!serviceId;
    try {
      if (isService && billLaterCommandId) {
        logBI(10570);
        await api.billCommand.remove(agreement.id, serviceId, billLaterCommandId);
      } else {
        logBI(10567);
        await api.charge.delete(agreement.id, chargeId);
      }
      await refreshAllData();
      globalContext.showSuccessToast(isService ? 'Service was removed from upcoming bill' : 'Charge was removed', 'remove-success-toast');
    } catch (err) {
      globalContext.showErrorToast(isService ? 'Failed to remove service from upcoming bill' : 'Failed to remove charge', 'remove-failure-toast');
    } finally {
      globalContext.stopLoader(loaderName);
    }
  }
  async function refreshAllData(options) {
    await Promise.all([refreshInvoiceDraft(options), refreshBillingHubData(options)]);
  }
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": "billing-hub"
  }, /*#__PURE__*/React.createElement(MinisiteTabHeader, {
    text: invoiceDraftDates.length > 0 && isVendor && /*#__PURE__*/React.createElement(InvoiceNavigator, {
      value: selectedDate,
      options: invoiceDraftDates,
      onChange: date => {
        lineItemIds.current = undefined;
        setSelectedDate(date);
      }
    }),
    actions: isVendor && /*#__PURE__*/React.createElement(Button, {
      skin: "primaryOutline",
      disabled: DISABLE_BILLING_SCAN || loadingInvoiceDraft,
      onClick: () => setIsDrawerOpen(true),
      "data-testid": "manage-billing-button",
      tooltip: DISABLE_BILLING_SCAN ? {
        title: /*#__PURE__*/React.createElement(DisabledBillingTooltip, null),
        placement: 'left'
      } : undefined
    }, "Billing Control")
  }), isVendor && !isActiveAgreement && /*#__PURE__*/React.createElement(Callout, {
    className: styles.callout,
    "data-testid": "bh-future-agreement-warning"
  }, "The agreement takes effect on ", timeModel.formatDate(agreement.effectiveDate), ". Until then, you can add or remove services from future invoices using the Billing Control."), !isVendor && servicesRequiringDeliveryApproval.map(s => /*#__PURE__*/React.createElement(Callout, {
    key: s.id,
    intent: "warning",
    className: styles.callout,
    withAction: /*#__PURE__*/React.createElement(Button, _extends({
      size: "small",
      skin: "primaryOutline",
      "data-testid": "bh-view-delivery-".concat(s.id),
      as: Link,
      to: routes.deliveryApproval(agreement.id, s.id)
    }, openInNewTabProps), "Review files")
  }, "You have files awaiting approval")), /*#__PURE__*/React.createElement(Drawer, {
    open: isDrawerOpen,
    header: /*#__PURE__*/React.createElement(Typography, {
      variant: "h6",
      weight: "normal"
    }, "Billing Control"),
    footer: /*#__PURE__*/React.createElement(BillingControlFooter, {
      agreement: agreement,
      invoiceDateInfo: invoiceDateInfo,
      totalCredits: billingControlData.totalCredits,
      onRefresh: refreshAllData
    }),
    onClose: () => setIsDrawerOpen(false),
    onCloseButtonClick: () => setIsDrawerOpen(false),
    BackdropProps: {
      invisible: true
    }
  }, /*#__PURE__*/React.createElement(BillingControl, {
    agreement: agreement,
    deliverablesBillCommandsMap: deliverablesBillCommandsMap,
    billingControlData: billingControlData,
    invoiceDateInfo: invoiceDateInfo,
    onRefresh: refreshAllData
  })), renderScheduledInvoice({
    strategy: RestfulStrategy.PreferLoading,
    renderChild: _ref3 => {
      let {
        data
      } = _ref3;
      return data.invoiceDraft ? /*#__PURE__*/React.createElement(ScheduledInvoice, {
        agreementId: agreement.id,
        isVendor: isVendor,
        issueDate: data.invoiceDraft.issueDate,
        paymentDate: data.invoiceDraft.paymentDate,
        rowsProps: getLineItemsProps(data.invoiceDraft, onApprove, onRemove),
        addedLineItemId: data.addedLineItemId,
        totalAmount: data.invoiceDraft.totalAmount,
        onRefresh: () => Promise.resolve(undefined)
      }) : /*#__PURE__*/React.createElement(Typography, {
        className: styles.empty,
        "data-testid": "no-invoices"
      }, getNoInvoicesText(selectedDate, isVendor));
    },
    renderError: () => /*#__PURE__*/React.createElement(GenericError, {
      testid: "error-boundary-page"
    }),
    renderLoader: () => /*#__PURE__*/React.createElement("div", {
      className: styles.loader,
      "data-testid": "invoice-draft-loading"
    }, /*#__PURE__*/React.createElement(Spinner, null))
  }));
}
export default function BillingHub(props) {
  const {
    agreement,
    isVendor,
    refreshAgreement
  } = props;
  const {
    api
  } = useStoreActions();
  const emptyState = getEmptyState(agreement);
  useEffect(() => {
    document.title = 'Billing Hub';
  }, []);
  async function fetchBillingHubData() {
    const [deliverablesBillCommandsMap, invoiceDraftDates, billingControlData] = await Promise.all([fetchDeliverablesBillCommandsMap(), api.invoiceDraft.getDates(agreement.id), isVendor ? api.relationship.getBillingControlData(agreement.id) : Promise.resolve(billingControlDataModel.make())]);
    return {
      deliverablesBillCommandsMap,
      invoiceDraftDates,
      billingControlData
    };
  }
  async function fetchDeliverablesBillCommandsMap() {
    const deliveryServices = agreement.services.filter(s => isServiceRelevantForBillingHub(s) && serviceModel.isOneTimeDelivery(s.billing));
    const deliverablesBillCommands = await Promise.all(deliveryServices.map(s => api.billCommand.get(agreement.id, s.id)));
    const map = {};
    deliverablesBillCommands.forEach((billCommands, idx) => {
      map[deliveryServices[idx].id] = billCommands || [];
    });
    return map;
  }
  return emptyState ? /*#__PURE__*/React.createElement(EmptyState, {
    testid: "billing-hub-empty-state",
    illustration: "UpcomingBill",
    title: emptyState.text,
    paragraphs: emptyState.paragraphs
  }) : /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: fetchBillingHubData,
    renderChild: _ref4 => {
      let {
        data,
        loading,
        refresh
      } = _ref4;
      return /*#__PURE__*/React.createElement(BillingHubWithData, {
        billingData: data,
        agreement: agreement,
        isVendor: isVendor,
        isRefreshing: loading,
        onRefresh: async options => {
          await Promise.all([refresh(options), refreshAgreement(options)]);
        }
      });
    }
  });
}